import React, { useState, useEffect } from "react";
import { API_URL } from '../config';
import axios from 'axios';

const ZakalkaDriverTripsPage = () => {
  const [activeTab, setActiveTab] = useState("my_trips");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Новые состояния для счетчика, фото и примечаний
  const [tripDetails, setTripDetails] = useState({}); // {tripId: {thickness: {4: 10, 6: 5}, photos: [], notes: ''}}
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Состояние для модального окна
  const [modalTrip, setModalTrip] = useState(null);

  // Загрузка рейсов
  const fetchTrips = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/zakalka-trips`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setTrips(response.data.trips);
      }
    } catch (err) {
      console.error("Ошибка загрузки рейсов:", err);
      setError(`Ошибка загрузки: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Функция принятия рейса водителем
  const handleAcceptTrip = async (tripId) => {
    if (!confirm('Принять рейс в работу?')) return;

    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/zakalka-trips/${tripId}/accept-by-driver`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert('Рейс принят в работу!');
        await fetchTrips();
      }
    } catch (err) {
      console.error("Ошибка принятия рейса:", err);
      alert(`Ошибка: ${err.response?.data?.message || err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Функция сдачи партнеру
  const handleDeliverToPartner = async (tripId) => {
    if (!confirm('Сдать рейс партнеру?')) return;

    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/zakalka-trips/${tripId}/deliver-to-partner`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert('Рейс сдан партнеру!');
        await fetchTrips();
      }
    } catch (err) {
      console.error("Ошибка сдачи партнеру:", err);
      alert(`Ошибка: ${err.response?.data?.message || err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Функция забора у партнера
  const handleReceiveFromPartner = async (tripId) => {
    if (!confirm('Забрать рейс у партнера?')) return;

    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/zakalka-trips/${tripId}/receive-from-partner`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert('Рейс забран у партнера!');
        await fetchTrips();
      }
    } catch (err) {
      console.error("Ошибка забора у партнера:", err);
      alert(`Ошибка: ${err.response?.data?.message || err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Загрузка деталей рейса с подсчетом по толщинам
  const fetchTripDetails = async (tripId) => {
    try {
      setLoadingDetails(true);
      const token = localStorage.getItem('token');

      // Загружаем детали, фото и примечания параллельно
      const [detailsResponse, photosResponse, notesResponse] = await Promise.all([
        axios.get(`${API_URL}/api/zakalka-trips/${tripId}/details`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/zakalka-trips/${tripId}/photos`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => ({ data: { photos: [] } })),
        axios.get(`${API_URL}/api/zakalka-trips/${tripId}/notes`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => ({ data: { notes: '' } }))
      ]);

      if (detailsResponse.data.success) {
        const details = detailsResponse.data.details;

        // Подсчитываем количество деталей по толщинам (с учетом quantity)
        const thicknessCount = {};
        details.forEach(detail => {
          const thickness = detail.thickness || 'Не указана';
          thicknessCount[thickness] = (thicknessCount[thickness] || 0) + (detail.quantity || 1);
        });

        // Сохраняем в состояние
        setTripDetails(prev => ({
          ...prev,
          [tripId]: {
            thickness: thicknessCount,
            photos: photosResponse.data.photos || [],
            notes: notesResponse.data.notes || '',
            allDetails: details
          }
        }));
      }
    } catch (err) {
      console.error("Ошибка загрузки деталей рейса:", err);
      setError(`Ошибка загрузки деталей: ${err.message}`);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Загрузка фото
  const handlePhotoUpload = async (tripId, file) => {
    try {
      setUploadingPhoto(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('tripId', tripId);

      const response = await axios.post(
        `${API_URL}/api/zakalka-trips/${tripId}/upload-photo`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        // Добавляем фото в состояние (в формате объекта)
        const newPhoto = {
          id: Date.now(),
          url: response.data.photoUrl
        };
        setTripDetails(prev => ({
          ...prev,
          [tripId]: {
            ...prev[tripId],
            photos: [...(prev[tripId]?.photos || []), newPhoto]
          }
        }));
        alert('Фото успешно загружено!');
      }
    } catch (err) {
      console.error("Ошибка загрузки фото:", err);
      alert(`Ошибка загрузки фото: ${err.response?.data?.message || err.message}`);
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Сохранение примечаний
  const handleSaveNotes = async (tripId, notes) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/zakalka-trips/${tripId}/save-notes`,
        { notes },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        setTripDetails(prev => ({
          ...prev,
          [tripId]: {
            ...prev[tripId],
            notes
          }
        }));
        alert('Примечания сохранены!');
      }
    } catch (err) {
      console.error("Ошибка сохранения примечаний:", err);
      alert(`Ошибка: ${err.response?.data?.message || err.message}`);
    }
  };

  useEffect(() => {
    fetchTrips();
  }, []);

  // Фильтрация рейсов по вкладкам
  const getFilteredTrips = () => {
    switch(activeTab) {
      case 'available':
        // Доступные для принятия
        return trips.filter(t => t.status === 'saved');
      case 'in_progress':
        // В работе
        return trips.filter(t => ['shipped', 'delivered_to_partner'].includes(t.status));
      case 'completed':
        // Завершенные
        return trips.filter(t => t.status === 'received_from_partner' || t.status === 'received');
      default:
        // Все рейсы
        return trips;
    }
  };

  const filteredTrips = getFilteredTrips();

  // Конфигурация статусов
  const statusConfig = {
    saved: { icon: '💾', label: 'Сохранен', color: 'bg-gray-100 text-gray-800' },
    shipped: { icon: '🚚', label: 'Принят водителем', color: 'bg-blue-100 text-blue-800' },
    delivered_to_partner: { icon: '🏭', label: 'У партнера', color: 'bg-yellow-100 text-yellow-800' },
    received_from_partner: { icon: '📦', label: 'Забран у партнера', color: 'bg-orange-100 text-orange-800' },
    received: { icon: '✅', label: 'Принят с закалки', color: 'bg-green-100 text-green-800' }
  };

  return (
    <div className="container mx-auto p-4 max-w-6xl">
      <h1 className="text-2xl font-bold mb-6">Рейсы закалка - Водитель</h1>

      {/* Вкладки */}
      <div className="mb-6 border-b border-gray-200">
        <ul className="flex flex-wrap -mb-px text-sm">
          <li className="mr-2">
            <button
              className={`inline-block p-4 font-medium ${
                activeTab === "available"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("available")}
            >
              📋 Доступные ({trips.filter(t => t.status === 'saved').length})
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block p-4 font-medium ${
                activeTab === "in_progress"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("in_progress")}
            >
              🚚 В работе ({trips.filter(t => ['shipped', 'delivered_to_partner'].includes(t.status)).length})
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block p-4 font-medium ${
                activeTab === "completed"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("completed")}
            >
              ✅ Завершенные ({trips.filter(t => t.status === 'received_from_partner' || t.status === 'received').length})
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block p-4 font-medium ${
                activeTab === "my_trips"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("my_trips")}
            >
              🗂️ Все рейсы ({trips.length})
            </button>
          </li>
        </ul>
      </div>

      {/* Отображение ошибок */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <strong>Ошибка:</strong> {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ✕
          </button>
        </div>
      )}

      {/* Список рейсов */}
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Загрузка рейсов...</p>
        </div>
      ) : filteredTrips.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p className="text-lg">Рейсов не найдено</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTrips.map(trip => {
            const status = statusConfig[trip.status] || statusConfig.saved;

            return (
              <div
                key={trip.id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-base font-semibold text-gray-800">
                        Рейс №{trip.tripNumber}
                      </h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${status.color}`}>
                        {status.icon} {status.label}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        trip.type === 'shipping' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {trip.type === 'shipping' ? 'Отгрузка' : 'Прием'}
                      </span>
                    </div>

                    <div className="text-xs text-gray-600 space-y-1">
                      <p><strong>Партнер:</strong> {trip.partner || 'Не указан'}</p>
                      <p><strong>Создан:</strong> {new Date(trip.createdDate).toLocaleString('ru-RU')}</p>
                      {trip.shippedDate && <p><strong>Принят водителем:</strong> {new Date(trip.shippedDate).toLocaleString('ru-RU')}</p>}
                      {trip.deliveredToPartnerDate && <p><strong>Сдан партнеру:</strong> {new Date(trip.deliveredToPartnerDate).toLocaleString('ru-RU')}</p>}
                      {trip.receivedFromPartnerDate && <p><strong>Забран у партнера:</strong> {new Date(trip.receivedFromPartnerDate).toLocaleString('ru-RU')}</p>}
                      <p><strong>Заказов:</strong> {trip.totalOrders} | <strong>Деталей:</strong> {trip.totalDetails}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        setModalTrip(trip);
                        // Загружаем детали сразу при открытии
                        if (!tripDetails[trip.id]) {
                          fetchTripDetails(trip.id);
                        }
                      }}
                      className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                    >
                      Просмотр
                    </button>
                  </div>
                </div>

              </div>
            );
          })}
        </div>
      )}

      {/* Модальное окно просмотра рейса */}
      {modalTrip && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Заголовок модального окна */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  Рейс №{modalTrip.tripNumber}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${statusConfig[modalTrip.status]?.color || 'bg-gray-100 text-gray-800'}`}>
                    {statusConfig[modalTrip.status]?.icon} {statusConfig[modalTrip.status]?.label}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    modalTrip.type === 'shipping' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
                  }`}>
                    {modalTrip.type === 'shipping' ? 'Отгрузка' : 'Прием'}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setModalTrip(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>

            {/* Содержимое модального окна */}
            <div className="p-6 space-y-4">
              {/* Индикатор загрузки */}
              {loadingDetails && !tripDetails[modalTrip.id] && (
                <div className="text-center py-8">
                  <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                  <p className="mt-2 text-sm text-gray-600">Загрузка деталей...</p>
                </div>
              )}

              {/* Основная информация */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-3">ℹ️ Информация о рейсе</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
                  <div><strong>ID рейса:</strong> {modalTrip.id}</div>
                  <div><strong>Номер рейса:</strong> {modalTrip.tripNumber}</div>
                  <div><strong>Тип:</strong> {modalTrip.type === 'shipping' ? 'Отгрузка' : 'Прием'}</div>
                  <div><strong>Партнер:</strong> {modalTrip.partner || 'Не указан'}</div>
                  <div><strong>Заказов:</strong> {modalTrip.totalOrders}</div>
                  <div><strong>Деталей:</strong> {modalTrip.totalDetails}</div>
                  <div><strong>Создан:</strong> {new Date(modalTrip.createdDate).toLocaleString('ru-RU')}</div>
                  <div><strong>Создан пользователем:</strong> {modalTrip.createdBy || 'Не указан'}</div>
                  {modalTrip.shippedDate && <div><strong>Принят водителем:</strong> {new Date(modalTrip.shippedDate).toLocaleString('ru-RU')}</div>}
                  {modalTrip.deliveredToPartnerDate && <div><strong>Сдан партнеру:</strong> {new Date(modalTrip.deliveredToPartnerDate).toLocaleString('ru-RU')}</div>}
                  {modalTrip.receivedFromPartnerDate && <div><strong>Забран у партнера:</strong> {new Date(modalTrip.receivedFromPartnerDate).toLocaleString('ru-RU')}</div>}
                </div>
              </div>

              {/* Счетчик по толщинам */}
              {tripDetails[modalTrip.id]?.thickness && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-3">📏 Статистика по толщинам</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-3">
                    {Object.entries(tripDetails[modalTrip.id].thickness)
                      .sort(([a], [b]) => {
                        const numA = parseFloat(a);
                        const numB = parseFloat(b);
                        if (isNaN(numA)) return 1;
                        if (isNaN(numB)) return -1;
                        return numA - numB;
                      })
                      .map(([thickness, count]) => (
                        <div key={thickness} className="bg-white p-3 rounded-lg border border-blue-200 text-center shadow-sm">
                          <p className="text-xs text-gray-500">Толщина</p>
                          <p className="text-xl font-bold text-blue-600">{thickness}мм</p>
                          <p className="text-xs text-gray-600">{count} шт</p>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Загрузка фото */}
              {tripDetails[modalTrip.id] && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-3">📷 Фотографии</p>

                  {/* Форма загрузки */}
                  <div className="mb-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handlePhotoUpload(modalTrip.id, file);
                      }}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded file:border-0
                        file:text-sm file:font-semibold
                        file:bg-green-500 file:text-white
                        hover:file:bg-green-600
                        cursor-pointer"
                      disabled={uploadingPhoto}
                    />
                    {uploadingPhoto && <p className="text-xs text-gray-500 mt-1">Загрузка...</p>}
                  </div>

                  {/* Галерея фото */}
                  {tripDetails[modalTrip.id].photos && tripDetails[modalTrip.id].photos.length > 0 ? (
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                      {tripDetails[modalTrip.id].photos.map((photo, index) => {
                        const photoUrl = typeof photo === 'string' ? photo : (photo.url || photo.file_path);
                        return (
                          <img
                            key={photo.id || index}
                            src={`${API_URL}${photoUrl}`}
                            alt={`Фото ${index + 1}`}
                            className="w-full h-28 object-cover rounded border border-gray-200 cursor-pointer hover:opacity-75 transition-opacity"
                            onClick={() => window.open(`${API_URL}${photoUrl}`, '_blank')}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-2">Фотографий пока нет</p>
                  )}
                </div>
              )}

              {/* Примечания */}
              {tripDetails[modalTrip.id] && (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-3">📝 Примечания</p>
                  <textarea
                    value={tripDetails[modalTrip.id].notes || ''}
                    onChange={(e) => {
                      setTripDetails(prev => ({
                        ...prev,
                        [modalTrip.id]: {
                          ...prev[modalTrip.id],
                          notes: e.target.value
                        }
                      }));
                    }}
                    placeholder="Введите примечания к рейсу..."
                    className="w-full p-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    rows={4}
                  />
                  <button
                    onClick={() => handleSaveNotes(modalTrip.id, tripDetails[modalTrip.id].notes)}
                    className="mt-2 px-4 py-2 bg-yellow-500 text-white text-sm rounded-lg hover:bg-yellow-600 transition-colors"
                  >
                    Сохранить примечания
                  </button>
                </div>
              )}

              {/* Кнопки действий */}
              <div className="bg-gray-50 p-4 rounded-lg border-t border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-3">🔧 Действия</p>
                <div className="flex flex-wrap gap-2">
                  {/* Кнопка для статуса "saved" */}
                  {modalTrip.status === 'saved' && (
                    <button
                      onClick={async () => {
                        await handleAcceptTrip(modalTrip.id);
                        setModalTrip(null);
                      }}
                      disabled={actionLoading}
                      className="px-4 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                    >
                      🚚 Принять в работу
                    </button>
                  )}

                  {/* Кнопка для статуса "shipped" */}
                  {modalTrip.status === 'shipped' && (
                    <button
                      onClick={async () => {
                        await handleDeliverToPartner(modalTrip.id);
                        setModalTrip(null);
                      }}
                      disabled={actionLoading}
                      className="px-4 py-2 text-sm bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50"
                    >
                      🏭 Сдать партнеру
                    </button>
                  )}

                  {/* Кнопка для статуса "delivered_to_partner" */}
                  {modalTrip.status === 'delivered_to_partner' && (
                    <button
                      onClick={async () => {
                        await handleReceiveFromPartner(modalTrip.id);
                        setModalTrip(null);
                      }}
                      disabled={actionLoading}
                      className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                    >
                      📦 Забрать у партнера
                    </button>
                  )}

                  {/* Для статуса "received_from_partner" */}
                  {modalTrip.status === 'received_from_partner' && (
                    <div className="px-4 py-2 text-sm bg-orange-100 text-orange-800 rounded-lg border border-orange-200">
                      Ожидает приема на складе
                    </div>
                  )}

                  {/* Для статуса "received" */}
                  {modalTrip.status === 'received' && (
                    <div className="px-4 py-2 text-sm bg-green-100 text-green-800 rounded-lg border border-green-200">
                      ✅ Рейс завершен
                    </div>
                  )}

                  <button
                    onClick={() => setModalTrip(null)}
                    className="px-4 py-2 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors ml-auto"
                  >
                    Закрыть
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZakalkaDriverTripsPage;
